import DogQuestionImage from "@/assets/images/dog-question.png"
import { appStopUnstakingPopupState } from "@/atoms/appPopupState"
import { restakingAccountAtom } from "@/atoms/restakingState"
import { networkAtom, refreshRestakingAtom } from "@/atoms/settings"
import { ModalTxResult } from "@/components/modalTxResult"
import { RelendModal } from "@/components/relendModal"
import { STOP_UNSTAKING } from "@/utils/constants"
import { HelperText, TransactionDetail } from "@/utils/dataTypes"
import { stringifyVariable } from "@/utils/helpers"
import { formatToken } from "@/utils/numberFormatter"
import { Stack, Typography } from "@mui/material"
import { useRollbar } from "@rollbar/react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useState } from "react"
import {
  DEFAULT_HELPER_TEXT,
  DEFAULT_TRANSACTION_DETAIL,
  handleRestakingError,
  handleRestakingSuccessful,
} from "@/components/staking/components/detail/components/shared/commons"
import Image from "next/image"
import Button from "@/components/base/Button"
import useRestaking from "@/hooks/useRestaking"
import useTrans from "@/hooks/useTrans"

const View = () => {
  const trans = useTrans()
  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [helperText, setHelperText] = useState<HelperText>({ ...DEFAULT_HELPER_TEXT })
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({ ...DEFAULT_TRANSACTION_DETAIL })
  const rollbar = useRollbar()
  const { address, stakingItemInfo, unstakedAmountUI, stopUnstakingFn } = useRestaking()
  // for switching network
  const network = useAtomValue(networkAtom)

  const [popupState, setPopupState] = useAtom(appStopUnstakingPopupState)
  const restakingAccount = useAtomValue(restakingAccountAtom)
  const refreshStakingData = useSetAtom(refreshRestakingAtom)

  const handleCloseModal = () => {
    if (!loading) {
      setPopupState((prev) => ({ ...prev, isOpen: false }))
      setHelperText({ ...DEFAULT_HELPER_TEXT })
      setTransactionDetail({ ...DEFAULT_TRANSACTION_DETAIL })
    }
  }

  const handleStopUnstaking = async () => {
    setLoading(true)
    try {
      const result = await stopUnstakingFn()
      if (!result.isSucceeded) {
        handleRestakingError(
          unstakedAmountUI,
          stakingItemInfo?.symbol,
          restakingAccount,
          result,
          setHelperText,
          setTransactionDetail,
          rollbar,
          address,
          STOP_UNSTAKING,
          trans,
        )
        return
      }

      await handleRestakingSuccessful(
        network,
        unstakedAmountUI,
        stakingItemInfo?.symbol,
        result,
        setTransactionDetail,
        address,
        STOP_UNSTAKING,
        trans,
        refreshStakingData,
      )
    } catch (e: any) {
      setHelperText({ content: trans.staking.errors.stop_unstaking, color: "red" })
      rollbar.error(
        `RELEND: Error when claiming staking token. Error: Wallet address: ${address}. ${stringifyVariable(e)}`,
      )
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <RelendModal
      open={popupState.isOpen}
      handleClose={handleCloseModal}
      data-testid='modal-stop-unstaking'
      title={trans.staking.modal.confirm}
      showCloseButton
      hideButtonClose={transactionDetail.isActive}
      useDrawerOnMobile
      isSmallPopup
    >
      {transactionDetail.isActive && (
        <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
      )}
      {!transactionDetail.isActive && (
        <>
          <Stack alignItems='center' gap={1}>
            <Image src={DogQuestionImage} alt='doge-question' width={150} />

            <Typography className='text-center text-neutral-1000 font-bold sm:text-2xl'>
              {trans.staking.modal.stop_unstaking}
            </Typography>
            <Typography className='text-center text-neutral-900 sm:text-base'>
              {trans.staking.modal.are_you_sure_stop_unstake}{" "}
              <span className='font-semibold'>
                {formatToken(unstakedAmountUI)} {stakingItemInfo?.symbol}
              </span>
              ?
            </Typography>

            {helperText.content && (
              <Typography className='text-sm text-red-600 text-center'>{helperText.content}</Typography>
            )}

            <Button
              fullWidth
              variant='primary'
              size='lg'
              loading={loading}
              onClick={handleStopUnstaking}
              className='mt-4'
            >
              {trans.staking.modal.stop}
            </Button>
          </Stack>
        </>
      )}
    </RelendModal>
  )
}

export default View
